<script setup>
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import { defineEmits, ref, defineProps, computed } from "vue";
import { t } from "@/controllerLK/AvalLocalization";
import IconStars from "@/components/Icons/IconStars.vue";
import IconCheck from "@/components/Icons/IconCheck.vue";

const rubricStore = useRubricsStore();

const emit = defineEmits(["fix-query"]);

const props = defineProps({
  query: {
    type: String,
    default: "",
  }
});

const isLoadingFixQuery = ref(false);
const showSuccess = ref(false);
const isHovered = ref(false);

const showLabel = computed(() => isHovered.value && !isLoadingFixQuery.value && !showSuccess.value);

async function fixLuceneQuery() {
  isLoadingFixQuery.value = true;
  let result;
  try {
    const fixedQuery = await rubricStore.luceneExpressionFix(props.query);
    result = fixedQuery.result;
  } catch (e) {
    result = props.query;
  } finally {
    emit("fix-query", result);
    isLoadingFixQuery.value = false;
    showSuccess.value = true;

    setTimeout(() => {
      showSuccess.value = false;
    }, 1000);
  }
}
</script>

<template>
  <button
    class="expression-fix__button"
    :class="{ 'expression-fix__button--expanded': showLabel }"
    :disabled="isLoadingFixQuery || props.query.length === 0"
    type="button"
    @click="fixLuceneQuery"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <span
      v-if="showLabel"
      class="expression-fix__label"
    >
      {{ t('news_page_rubrics_edit_popup.button.lucene') }}
    </span>

    <span
      v-if="isLoadingFixQuery"
      class="expression-fix__spinner"
    />
    <IconCheck
      v-else-if="showSuccess"
      class="expression-fix__icon"
    />
    <IconStars
      v-else
      class="expression-fix__icon"
    />
  </button>
</template>

<style scoped lang="scss">
.expression-fix {
  &__button {
    position: absolute;
    bottom: 14px;
    right: 20px;

    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    min-width: 36px;
    min-height: 36px;
    width: 36px;
    height: 36px;
    border-radius: 50px;
    background-color: var(--fix-button-secondary-inactive-default);
    color: var(--icon-icon-primary);
    box-shadow: 0 4px 8px 0 #00000024;
    cursor: pointer;
    user-select: none;
    transition: width 0.3s ease, padding 0.3s ease, background 0.3s ease, color 0.3s ease;
    overflow: hidden;

    &--expanded {
      color: var(--background-bg-white);
      width: auto;
      padding: 0 14px;
      background: linear-gradient(74.42deg, #A0BFED 11.64%, #BCE0FC 36.15%, #C8CBE5 61.39%, #A0BFED 86.62%);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  &__label {
    white-space: nowrap;
  }

  &__icon {
    width: 17px;
    height: 17px;
    flex-shrink: 0;
  }

  &__spinner {
    border: 2px solid var(--icon-icon-blue);
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>